import { Button, Table, Tag } from 'antd';
import moment from 'moment';
import React from 'react';
import { ActionOption, ModuleName } from '../../../core/models/enum';
import { isUserAllowed } from '../../../helpers/CheckPermissionHelper';
import {
    errorWithUserMessage,
    popupConfirmDeleteWithCallback,
    success,
} from '../../../helpers/NotificationHelper';
import { ApiException, ApiTokensClient, ApiTokenVm } from '../../../utils/api';
import { Props } from './index';

interface State {
    tokens: ApiTokenVm[];
    loading: boolean;
    isUserAllowedToRevoke: boolean;
}

class ApiTokenTable extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            tokens: [],
            loading: true,
            isUserAllowedToRevoke: false,
        };
    }

    public componentDidMount = (): void => {
        this.getPermissions();
        this.refresh();
    };

    private getPermissions = (): void => {
        const { userProfile } = this.props;

        this.setState({
            isUserAllowedToRevoke: isUserAllowed(
                userProfile,
                ModuleName.ApiTokens,
                ActionOption.Update
            ),
        });
    };

    // Public so the parent page can reload the list after creating a token.
    public refresh = async (): Promise<void> => {
        this.setState({ loading: true });

        try {
            const client = new ApiTokensClient();
            const tokens = await client.getAll();
            this.setState({ tokens, loading: false });
        } catch (error) {
            this.setState({ loading: false });
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error loading tokens.');
            }
        }
    };

    private handleRevoke = async (id: number): Promise<void> => {
        try {
            const client = new ApiTokensClient();
            await client.revoke(id);
            success('Token revoked.');
            this.refresh();
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error revoking token.');
            }
        }
    };

    private renderStatus = (token: ApiTokenVm): React.ReactElement => {
        if (token.isRevoked) {
            return <Tag color="red">Revoked</Tag>;
        }
        if (token.isExpired) {
            return <Tag color="orange">Expired</Tag>;
        }
        return <Tag color="green">Active</Tag>;
    };

    public render(): React.ReactElement {
        const { tokens, loading, isUserAllowedToRevoke } = this.state;

        const columns = [
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Role',
                dataIndex: 'roles',
                key: 'roles',
            },
            {
                title: 'Created',
                dataIndex: 'createdDate',
                key: 'createdDate',
                render: (value: Date): string =>
                    value ? moment(value).format('YYYY-MM-DD HH:mm') : '-',
            },
            {
                title: 'Expires',
                dataIndex: 'expiresAt',
                key: 'expiresAt',
                render: (value: Date): string => (value ? moment(value).format('YYYY-MM-DD') : '-'),
            },
            {
                title: 'Status',
                key: 'status',
                render: (value: ApiTokenVm): React.ReactElement => this.renderStatus(value),
                width: 110,
            },
            {
                title: '',
                key: 'actions',
                width: 100,
                render: (value: ApiTokenVm): React.ReactElement =>
                    isUserAllowedToRevoke && value && value.isActive ? (
                        <Button
                            danger
                            size="small"
                            onClick={(): void =>
                                popupConfirmDeleteWithCallback(
                                    (): Promise<void> => this.handleRevoke(value.id),
                                    undefined,
                                    'Revoke this token? Any client using it will stop working immediately.',
                                    'Revoke'
                                )
                            }
                        >
                            Revoke
                        </Button>
                    ) : (
                        <span />
                    ),
            },
        ];

        return (
            <Table
                columns={columns}
                loading={loading}
                dataSource={tokens}
                rowKey={(record): string => (record && record.id ? record.id.toString() : '-1')}
                bordered
                size="small"
            />
        );
    }
}

export default ApiTokenTable;
